/**
 * Lookahead step sequencer, the standard Web Audio pattern: a setTimeout
 * poller (~25ms) that schedules any grid steps falling within the next
 * ~100ms window using the AudioContext's own sample-accurate clock, instead
 * of a naive setInterval-per-note (which drifts under JS timer jitter).
 *
 * Generic over the step callback so one Sequencer can drive an entire track
 * (bass + arp + hats all read from the same step index).
 */

export type StepCallback = (step: number, time: number, stepDurSec: number) => void;

const LOOKAHEAD_MS = 25;
const SCHEDULE_AHEAD_SEC = 0.1;

export class Sequencer {
  private timerId: number | null = null;
  private nextStepTime = 0;
  private step = 0;

  constructor(
    private readonly ctx: AudioContext,
    /** steps per bar (16 = sixteenth-note grid) */
    private readonly stepsPerBar: number,
    private readonly getBpm: () => number,
    private readonly onStep: StepCallback,
  ) {}

  get stepDurationSec(): number {
    const bpm = Math.max(20, this.getBpm());
    const quarterSec = 60 / bpm;
    return quarterSec * (4 / this.stepsPerBar);
  }

  get running(): boolean {
    return this.timerId !== null;
  }

  start(): void {
    if (this.timerId !== null) return;
    this.step = 0;
    this.nextStepTime = this.ctx.currentTime + 0.05;
    this.poll();
  }

  stop(): void {
    if (this.timerId !== null) {
      window.clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  private poll = (): void => {
    while (this.nextStepTime < this.ctx.currentTime + SCHEDULE_AHEAD_SEC) {
      const dur = this.stepDurationSec;
      this.onStep(this.step, this.nextStepTime, dur);
      this.nextStepTime += dur;
      this.step++;
    }
    this.timerId = window.setTimeout(this.poll, LOOKAHEAD_MS);
  };
}
